<script setup lang="ts">
import type { IMenuItem } from "@/types"
import logo from "@brand-logo"
import { useMutation } from "@tanstack/vue-query"
import { computed, onMounted, onUnmounted, ref } from "vue"
import { useRouter } from "vue-router"
import { Footer, FreemiumUpgradeCard, ResourcesSection } from "@/components"
import { getBrandFooterUrls } from "@/components/Footer/footer.constants"
import { AppLink, Button, Expandable, Icon } from "@/components/ui"
import { useToast } from "@/components/ui/ToastReka/useToast"
import { useTranslation } from "@/composables"
import { useAuthStore } from "@/stores/authStore"
import { UserRole } from "@/types"
import { getCreateEventCTA } from "@/utils"

withDefaults(defineProps<{
  upgradeUrl?: string
  isFreemiumEvent?: boolean
}>(), { isFreemiumEvent: false })

const router = useRouter()
const authStore = useAuthStore()
const toast = useToast()

const { t } = useTranslation()

const isSidebarOpen = ref(false)
const isMobile = ref(false)

function openSidebar() {
  isSidebarOpen.value = true
}

function closeSidebar() {
  isSidebarOpen.value = false
}

function checkMobile() {
  isMobile.value = window.innerWidth < 768
  if (!isMobile.value) {
    isSidebarOpen.value = false
  }
}

onMounted(() => {
  checkMobile()
  window.addEventListener("resize", checkMobile)
})

onUnmounted(() => {
  window.removeEventListener("resize", checkMobile)
})

const { mutate } = useMutation({
  mutationFn: () => authStore.logout(),
  onError: () => toast.error(),
  onSuccess: () => {
    toast.success({
      title: t("success"),
      description: t("host_nav.logout_success"),
    })

    router.push("/login")
  },
})

const hostLinks = computed<IMenuItem[]>(() => [
  { key: "packages", label: t("sidebar_host_layout.link_label.packages"), isHidden: !authStore.isAuthenticated, children: [
    { key: "packages-list", label: t("sidebar_host_layout.link_label.packages_list"), to: "/event/dashboard" },
    { key: "packages-create", label: getCreateEventCTA(authStore.user).label, href: getCreateEventCTA(authStore.user).href, to: getCreateEventCTA(authStore.user).to },
  ] },
  { key: "account", label: t("sidebar_host_layout.link_label.account"), isHidden: !authStore.isAuthenticated, children: [
    { key: "profile", label: t("sidebar_host_layout.link_label.profile"), to: "/profile" },
    { key: "logout", label: t("sidebar_host_layout.link_label.logout"), onClick: () => mutate() },
  ] },
  { key: "affiliate", label: t("sidebar_host_layout.link_label.affiliate"), href: "https://guestpix.com/affiliate-area-login/", isHidden: !authStore.isAuthenticated || !authStore.user?.roles?.includes(UserRole.Affiliate) },
  { key: "login", label: t("sidebar_host_layout.link_label.login"), to: "/login", isHidden: authStore.isAuthenticated },
  { key: "help", label: t("sidebar_host_layout.link_label.help"), href: getBrandFooterUrls().helpCenter },
])
</script>

<template>
  <div class="flex min-h-screen ml-0">
    <div v-if="isMobile && isSidebarOpen" class="fixed inset-0 bg-black/50 z-40" data-test="mobile-overlay" @click="closeSidebar" />

    <aside
      class="bg-tertiary flex flex-col transition-transform duration-300 ease-in-out z-50 fixed top-0 left-0 h-screen w-full max-w-[320px] md:sticky md:top-0 md:h-dvh md:w-70 md:max-w-none md:self-start"
      :class="[isMobile && (isSidebarOpen ? 'translate-x-0' : '-translate-x-full')]"
      data-test="sidebar"
    >
      <div class="flex flex-col flex-1 min-h-0 z-10">
        <div class="py-4 px-2 flex justify-between items-center shrink-0">
          <AppLink to="/event/dashboard">
            <img :src="logo" alt="Guestpix Logo" loading="lazy" class="h-14">
          </AppLink>
          <div class="md:hidden">
            <Button variant="flat" @click="closeSidebar">
              <Icon icon="Close" size="sm" />
              <span class="sr-only">{{ t('sidebar_host_layout.close_sidebar_sr') }}</span>
            </Button>
          </div>
        </div>

        <nav class="overflow-y-auto flex-1 font-comfortaa">
          <ul class="list-none m-0 p-0">
            <li v-for="link in hostLinks" :key="link.key" class="m-0" :class="link.isHidden ? 'hidden' : ''">
              <AppLink v-if="link.to" :to="link.to" class="font-bold flex items-center gap-3 py-3 px-6 no-underline transition-all duration-200 ease-in-out border-l-[3px] border-transparent hover:border-l-nav-active">
                {{ link.label }}
              </AppLink>

              <a v-else-if="link.href" :href="link.href" target="_blank" class="font-bold flex items-center gap-3 py-3 px-6 no-underline transition-all duration-200 ease-in-out border-l-[3px] border-transparent hover:border-l-nav-active">
                {{ link.label }}
              </a>

              <Expandable v-if="link.children">
                <template #activator="{ toggle, isOpen, activatorAttrs }">
                  <button v-bind="activatorAttrs" class="cursor-pointer font-bold flex items-center gap-2 px-6 py-4 no-underline transition-all duration-200 ease-in-out border-b-[3px] border-transparent relative" @click="toggle">
                    {{ link.label }}
                    <Icon
                      icon="CircleChevronDown"
                      class="transition-transform duration-200 ease-in-out w-6! h-6!"
                      :class="{ 'rotate-180': isOpen }"
                    />
                  </button>
                </template>
                <div v-for="child in link.children" :key="child.key">
                  <AppLink v-if="child.to" :to="child.to ?? '#'" class="flex items-center gap-3 py-3 px-6 pl-10 no-underline transition-all duration-200 ease-in-out border-l-[3px] border-transparent hover:font-bold hover:border-l-nav-active">
                    {{ child.label }}
                  </AppLink>
                  <a v-else-if="child.href" :href="child.href" target="_blank" class="flex items-center gap-3 py-3 px-6 pl-10 no-underline transition-all duration-200 ease-in-out border-l-[3px] border-transparent hover:font-bold hover:border-l-nav-active">
                    {{ child.label }}
                  </a>
                  <button v-else-if="child.onClick" class="flex items-center gap-3 py-3 px-6 pl-10 no-underline transition-all duration-200 ease-in-out border-l-[3px] border-transparent hover:font-bold hover:border-l-nav-active" @click="child.onClick">
                    {{ child.label }}
                  </button>
                </div>
              </Expandable>
            </li>
          </ul>
        </nav>

        <FreemiumUpgradeCard v-if="upgradeUrl" class="p-4" :upgrade-url="upgradeUrl" />
      </div>
    </aside>

    <main class="flex-1 flex flex-col min-w-0" data-test="main-content">
      <header class="flex items-center gap-4 py-4 px-2 md:px-6 bg-white sticky top-0 z-20 border-b border-secondary">
        <Button v-if="isMobile" variant="flat" data-test="mobile-menu-button" @click="openSidebar">
          <Icon icon="CircleMenu" size="lg" class="shrink-0 fill-primary text-primary" />
          <span class="sr-only">{{ t('sidebar_host_layout.open_sidebar_sr') }}</span>
        </Button>

        <div class="text-xl md:text-2xl font-bold font-comfortaa w-full">
          <slot name="header">
            <img :src="logo" alt="Guestpix Logo" loading="lazy" class="h-11">
          </slot>
        </div>
      </header>

      <div class="flex-1 bg-white">
        <slot name="content" />
        <ResourcesSection class="border-t" />
      </div>

      <footer>
        <Footer :is-freemium-event="isFreemiumEvent" />
      </footer>
    </main>
  </div>
</template>
